import { useMemo } from 'react';
import { cloneDeep } from 'lodash';

import { ResourceType } from '@dbt-labs/dbt-dag';

import { graphql } from '@/__generated__';
import {
  EnvironmentAppliedNestedNode,
  EnvironmentAppliedNode,
  EnvironmentAppliedNodeEdge,
  PublicModelNode,
} from '@/__generated__/graphql';
import { ExplorerDagNode } from '@/components/ExplorerDag';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { ParsedUniqueId, parseUniqueId } from '@/typings/parseUniqueId';
import { truthy } from '@/util/array';
import { toAppliedLineageNode } from '../dbtStrategy/useAppliedLineage';
import { useResourceParents } from '../dbtStrategy/useResourceParents';
import { ExternalConnections, useDependentProjects } from '../useDependentProjects';
import { useAllPublicModels } from './publicModels';
import { useProducerProjects } from './relatedProjects';
import { DbtResponse, useGenericResource } from './types';

export const GetAppliedResourceLineageParents = graphql(/* GraphQL */ `
  query GetAppliedResourceLineageParents(
    $environmentId: BigInt!
    $filter: AppliedResourcesFilter!
    $size: Int!
  ) {
    environment(id: $environmentId) {
      applied {
        resources(first: $size, filter: $filter) {
          edges {
            node {
              uniqueId
              projectId
              environmentId
              ... on ModelAppliedStateNode {
                materializedType
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on ExposureAppliedStateNode {
                name # needed for exposure to appear in dag with label
                autoBiProvider
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on TestAppliedStateNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on SnapshotAppliedStateNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on MetricDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on SemanticModelDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on SavedQueryDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
            }
          }
        }
      }
    }
  }
`);

export const GetDefinitionResourceLineageParents = graphql(/* GraphQL */ `
  query GetDefinitionResourceLineageParents(
    $environmentId: BigInt!
    $filter: DefinitionResourcesFilter!
    $size: Int!
  ) {
    environment(id: $environmentId) {
      definition {
        resources(first: $size, filter: $filter) {
          edges {
            node {
              uniqueId
              projectId
              environmentId
              ... on MetricDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on TestDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on MacroDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on MetricDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on ExposureDefinitionNode {
                name # needed for exposure to appear in dag with label
                autoBiProvider
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on SnapshotDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on SavedQueryDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
              ... on SemanticModelDefinitionNode {
                parents {
                  uniqueId
                  __typename
                }
              }
            }
          }
        }
      }
    }
  }
`);

type RawProjectNode = {
  parentIds: string[];
  resourceType: 'project';
  name: string;
  projectId: number;
  uniqueId: string;
  environmentId: number;
  hasReferences: boolean;
  projectExpandHandler?: () => void;
};

type RawLineageNode = ReturnType<typeof useGenericResource>['data'] & {
  parentIds?: string[];
  publicParentIds?: string[];
};

type SimplifiedLineageNode = {
  uniqueId: string;
  name: string;
  resourceType: ResourceType;
  __typename: string;
};

type EnvironmentAppliedNodeMaybeWithParents = EnvironmentAppliedNode & {
  parents?: EnvironmentAppliedNestedNode[];
};

const getParentsFromEdges = (edges: EnvironmentAppliedNodeEdge[], id: string) => {
  const node = edges.find((edge) => edge.node.uniqueId === id)
    ?.node as EnvironmentAppliedNodeMaybeWithParents;
  return getParentsFromNode(node);
};

const getParentsFromNode = (node: EnvironmentAppliedNodeMaybeWithParents) => {
  if (!node || !node?.parents) return [];
  return node.parents.map((parent) => parent.uniqueId);
};

const hydrateResource = (
  node: SimplifiedLineageNode,
  publicModel?:
    | NonNullable<PublicModelNode>
    | NonNullable<EnvironmentAppliedNodeEdge>['node'],
): RawLineageNode | {} => {
  if (!publicModel) return {};
  const parsedId = parseUniqueId(node.uniqueId);
  return {
    ...node,
    access: 'public',
    name: parsedId.remainder,
    uniqueId: parsedId.raw,
    projectId: publicModel.projectId,
    environmentId: publicModel.environmentId!,
    environmentDeploymentType:
      'environmentDeploymentType' in publicModel
        ? publicModel.environmentDeploymentType
        : null,
    alias: null,
    database: null,
    schema: null,
    filePath: '',
    parentIds: [],
    tags: [],
    group: null,
    publicParentIds: [],
    version: null,
    materializationType:
      'materializedType' in publicModel ? publicModel.materializedType : null,
    lastRunStatus: null,
    fqn: 'fqn' in publicModel ? publicModel.fqn : [],
  };
};

const hydrateParentIds = (node: RawLineageNode) => {
  const parentIds: string[] = [];
  const publicParentIds: string[] = [];
  node.parents?.forEach((parent) => {
    if (parent.__typename === 'ExternalModelNode') {
      publicParentIds.push(parent.uniqueId);
    } else {
      parentIds.push(parent.uniqueId);
    }
  });

  return {
    ...node,
    parentIds,
    publicParentIds,
  };
};

const nodeTypeMapping: Partial<Record<ResourceType, 'applied' | 'definition'>> = {
  exposure: 'applied',
  model: 'applied',
  seed: 'applied',
  snapshot: 'applied',
  source: 'applied',
  test: 'applied',
  unit_test: 'applied',
  macro: 'definition',
  metric: 'definition',
  semantic_model: 'definition',
  saved_query: 'definition',
  group: 'definition',
};

export const useResourceWithParentsAndChildren = (
  _uniqueId: ParsedUniqueId | undefined | null,
  { pause }: { pause?: boolean } = {},
): DbtResponse<ExplorerDagNode[]> & {
  adapterType: string;
  status: 'loading' | 'error' | 'success' | 'paused';
} => {
  const { projectId, environmentId, accountId } = useDbtCloudProject();
  const { getProjectName } = useDbtCloudAccount();
  const uniqueId = pause ? undefined : (_uniqueId ?? undefined);

  const lineageQuery = useGenericResource(uniqueId, { pause });

  const matchingType = uniqueId?.resourceType
    ? nodeTypeMapping[uniqueId.resourceType]
    : undefined;

  const node = lineageQuery.data;

  // separate uniqueIds for parents and children to fetch their parents
  const uniqueIds = node
    ? [
        ...(node.children ?? []).map((child) => child.uniqueId),
        ...(node.parents ?? []).map((parent) => parent.uniqueId),
      ]
    : [];

  // fetch all parents of child and parent resources
  const lineageParentsQuery = useResourceParents(uniqueIds, matchingType);

  const otherDependentProjectsQuery = useDependentProjects(environmentId);

  const upstreamPublicModels =
    node?.parents
      ?.filter((parent) => parent.__typename === 'ExternalModelNode')
      .map((publicModel) => publicModel.uniqueId) || [];

  const producerProjectsQuery = useProducerProjects({
    environmentId,
    pause: !upstreamPublicModels?.length,
  });
  const producerEnvironmentIds = producerProjectsQuery.data?.producerProjects?.map(
    (project) => project.producerEnvironmentId,
  );
  const filteredProducerEnvironmentIds = producerEnvironmentIds?.filter(truthy);

  // use new envid filter in usePublicModels
  const publicModelsQuery = useAllPublicModels({
    loadingStrategy: 'waitForAllResults',
    accountId,
    environmentIds: filteredProducerEnvironmentIds,
    pause: !upstreamPublicModels?.length,
  });

  return useMemo(() => {
    if (
      pause ||
      !node ||
      lineageQuery.fetching ||
      lineageParentsQuery.fetching ||
      otherDependentProjectsQuery.fetching ||
      publicModelsQuery.fetching ||
      (!!upstreamPublicModels?.length && producerProjectsQuery.fetching)
    ) {
      return {
        data: undefined,
        status: pause ? 'paused' : ('loading' as const),
        adapterType: '',
        fetching: true,
        error: undefined,
      };
    }

    const copiedNode: typeof node = cloneDeep(node);
    copiedNode.parents = copiedNode.parents || [];
    copiedNode.children = copiedNode.children || [];

    const nodes = [
      {
        // only root node needs to be hydrated
        ...hydrateParentIds(copiedNode),
        environmentId: environmentId,
        materializationType: node.materializedType,
      },
      // hydrate parents
      ...copiedNode.parents.map((parent) => ({
        ...parent,
        environmentId,
        ...hydrateResource(
          parent as SimplifiedLineageNode,
          // it's either a public model or a normal resource
          publicModelsQuery?.data?.find(
            (publicModel) => publicModel.uniqueId === parent.uniqueId,
          ) ||
            lineageParentsQuery.data?.edges.find(
              (edge) => edge.node.uniqueId === parent.uniqueId,
            )?.node,
        ),
        parentIds: getParentsFromEdges(
          (lineageParentsQuery.data?.edges || []) as EnvironmentAppliedNodeEdge[],
          parent.uniqueId,
        ),
      })),
      // hydrate children
      ...copiedNode.children.map((child) => {
        const childFromLineageParentsQuery = lineageParentsQuery.data?.edges.find(
          (edge) => edge.node.uniqueId === child.uniqueId,
        )?.node;
        return {
          ...child,
          ...(childFromLineageParentsQuery
            ? {
                ...childFromLineageParentsQuery,
                parentIds: getParentsFromNode(
                  childFromLineageParentsQuery as EnvironmentAppliedNodeMaybeWithParents,
                ),
              }
            : {
                environmentId,
                projectId,
                parentIds: [],
              }),
        };
      }),
    ];

    const downstreamProjects: Record<string, RawProjectNode> = {};
    nodes.forEach((node) => {
      const projectConnections = (otherDependentProjectsQuery.data ?? {})[
        node.uniqueId ?? ''
      ] as ExternalConnections | undefined;
      projectConnections?.children.forEach((project) => {
        const numDependentModels = project.numDependentModels || 0;
        downstreamProjects[project.id] ??= {
          parentIds: [],
          name: project.label,
          projectId: project.projectId,
          environmentId: project.environmentId!,
          uniqueId: project.id,
          resourceType: 'project',
          hasReferences: false,
        };
        if (numDependentModels > 0) {
          downstreamProjects[project.id].hasReferences = true;
          // only show edge from public model to downstream project
          // if the project directly references the public model
          downstreamProjects[project.id].parentIds.push(node.uniqueId ?? '');
        }
      });
    });

    Object.values(downstreamProjects).forEach((node) => {
      // only make the project node appear if it has references,
      // i.e. edge from public model to downstream project
      if (node.hasReferences) {
        nodes?.push(node as any);
      }
    });

    const status = lineageQuery.fetching
      ? 'loading'
      : lineageQuery.error
        ? 'error'
        : 'success';

    return {
      data: nodes?.map((node: any) =>
        toAppliedLineageNode(node, projectId, getProjectName),
      ),
      status: status,
      adapterType: '',
      fetching: lineageQuery.fetching || lineageParentsQuery.fetching,
      error: lineageQuery.error || lineageParentsQuery.error,
    };
  }, [
    pause,
    node,
    lineageQuery.fetching,
    lineageQuery.error,
    lineageParentsQuery.fetching,
    lineageParentsQuery.error,
    lineageParentsQuery.data?.edges,
    otherDependentProjectsQuery.fetching,
    otherDependentProjectsQuery.data,
    publicModelsQuery.fetching,
    publicModelsQuery?.data,
    upstreamPublicModels?.length,
    producerProjectsQuery.fetching,
    environmentId,
    projectId,
    getProjectName,
  ]);
};
